import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2, Pause, Play } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` } });
const fmt = (s) => (!isFinite(s) || s < 0 ? "0:00" : `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`);

/* Plays a ready-made signature right inside its sheet: no library entry, no page change. */
export const SheetPlayer = ({ bpId, accent, autoPlay }) => {
  const a = useRef(null);
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  useEffect(() => {
    axios.get(`${API}/api/audio-journeys/blueprints/${bpId}/listen`, auth())
      .then((r) => { setSrc(`${API}${r.data.url}`); setDur(r.data.duration_seconds || 0); })
      .catch(() => setErr(true));
  }, [bpId]);
  useEffect(() => { if (src && autoPlay) a.current?.play().catch(() => {}); }, [src, autoPlay]);
  const toggle = () => (a.current?.paused ? a.current.play().catch(() => {}) : a.current?.pause());
  const seek = (e) => {
    if (!a.current || !dur) return;
    const r = e.currentTarget.getBoundingClientRect();
    a.current.currentTime = Math.max(0, Math.min(dur, ((e.clientX - r.left) / r.width) * dur));
  };
  if (err) return <p className="text-sm italic text-center" style={{ color: "rgba(245,236,217,0.6)" }} data-testid="sheet-player-error">This one can't play right now. Please try again shortly.</p>;
  return (
    <div className="flex items-center gap-4 rounded-full pl-2 pr-5 py-2" data-testid="sheet-player"
      style={{ background: "rgba(255,255,255,0.05)", border: `1px solid ${accent}66`, backdropFilter: "blur(14px)" }}>
      <button type="button" onClick={toggle} disabled={!src} aria-label={playing ? "Pause" : "Play"} data-testid="sheet-player-toggle"
        className="shrink-0 w-12 h-12 rounded-full inline-flex items-center justify-center transition-transform duration-200 active:scale-95"
        style={{ background: "#d7bb7a", color: "#0d1f3a" }}>
        {!src ? <Loader2 size={18} className="animate-spin" /> : playing ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" className="ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0">
        <div onClick={seek} className="h-1 rounded-full cursor-pointer relative" style={{ background: "rgba(215,187,122,0.22)" }} data-testid="sheet-player-seek">
          <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${dur ? (cur / dur) * 100 : 0}%`, background: "#d7bb7a" }} />
        </div>
        <div className="mt-1.5 flex justify-between text-[0.62rem] tabular-nums" style={{ color: "rgba(245,236,217,0.6)", fontFamily: '"Manrope", sans-serif' }}>
          <span data-testid="sheet-player-time">{fmt(cur)}</span><span>{fmt(dur)}</span>
        </div>
      </div>
      {src && <audio ref={a} src={src} preload="metadata" onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
        onTimeUpdate={(e) => setCur(e.currentTarget.currentTime)} onLoadedMetadata={(e) => isFinite(e.currentTarget.duration) && setDur(e.currentTarget.duration)} />}
    </div>
  );
};
